import { useDebounceFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { captureException } from '@/errors';
import {
  useFeatureFlagStore,
  useFlashesStore,
  useReportsStore,
} from '@/stores';
import { ReportSlackChannel } from '@/types/reports';

const SEARCH_DEBOUNCE_MS = 250;

export default function useSlackChannelSearch() {
  const reportsStore = useReportsStore();
  const { slackApp } = storeToRefs(reportsStore);
  const featureFlagStore = useFeatureFlagStore();
  const flashesStore = useFlashesStore();

  const channelSearchResults = ref<ReportSlackChannel[] | null>(null);
  const customLookupChannels = ref<ReportSlackChannel[]>([]);
  const isFetchingChannels = ref(false);
  const isLookingUpChannel = ref(false);
  const channelSearchQuery = ref('');
  let latestSearchId = 0;

  const isSlackCachingEnabled = computed(() =>
    featureFlagStore.hasFeatureFlag(FEATURE_FLAG.TEMP_SLACK_CACHING),
  );
  const syncStatus = computed(() => slackApp.value?.sync_status);
  const syncedAt = computed(() => slackApp.value?.synced_at ?? undefined);
  const isServerChannelSearch = computed(
    () => isSlackCachingEnabled.value && syncStatus.value !== undefined,
  );

  const cachedChannels = computed<ReportSlackChannel[]>(() =>
    isServerChannelSearch.value && channelSearchResults.value !== null
      ? channelSearchResults.value
      : (slackApp.value?.channel_list ?? []),
  );

  const needsReauth = computed(() => slackApp.value?.needs_reauth);
  const isChannelListSyncing = computed(
    () =>
      isServerChannelSearch.value &&
      (syncStatus.value === 'syncing' || !syncedAt.value),
  );
  const showSyncingState = computed(
    () => isChannelListSyncing.value && !needsReauth.value,
  );
  const hasNoSyncedChannels = computed(
    () =>
      isServerChannelSearch.value &&
      !needsReauth.value &&
      !!syncedAt.value &&
      !slackApp.value?.channel_list?.length,
  );
  const hasSyncError = computed(
    () =>
      isServerChannelSearch.value &&
      !needsReauth.value &&
      syncStatus.value === 'error',
  );

  async function searchChannels(search: string) {
    if (!isServerChannelSearch.value) return;
    const searchId = ++latestSearchId;
    isFetchingChannels.value = true;
    try {
      const { data, error } = await crossbeamApi.GET('/v0.1/slack-app', {
        params: { query: search ? { search } : {} },
      });
      if (searchId !== latestSearchId) return;
      if (error || !data) {
        captureException(error ?? new Error('Slack channel search failed'));
        flashesStore.addErrorFlash({
          message: "Couldn't search Slack channels, please try again",
        });
        return;
      }
      channelSearchResults.value = data.channel_list ?? [];
    } finally {
      if (searchId === latestSearchId) isFetchingChannels.value = false;
    }
  }

  const debouncedSearchChannels = useDebounceFn(
    (search: string) => searchChannels(search),
    SEARCH_DEBOUNCE_MS,
  );

  function onChannelSearch(query: string) {
    channelSearchQuery.value = query.replace(/^#/, '');
    if (isServerChannelSearch.value) {
      debouncedSearchChannels(channelSearchQuery.value.trim());
    }
  }

  function resetSearch() {
    channelSearchQuery.value = '';
    if (isServerChannelSearch.value) debouncedSearchChannels('');
  }

  function clearCustomChannels() {
    customLookupChannels.value = [];
  }

  function isValidSlackChannelName(name: string) {
    return /^[a-z0-9][a-z0-9_-]*$/.test(name) && name.length <= 80;
  }

  async function lookUpCustomChannel(
    name: string,
    { requireShared = false }: { requireShared?: boolean } = {},
  ): Promise<ReportSlackChannel | null> {
    if (!isValidSlackChannelName(name)) {
      flashesStore.addErrorFlash({
        message:
          'Channel names may only contain lowercase letters, numbers, hyphens, and underscores',
      });
      return null;
    }
    isLookingUpChannel.value = true;
    try {
      const { data, error, response } = await crossbeamApi.GET(
        '/v0.1/slack-app/channel-by-name',
        { params: { query: { name } } },
      );
      const status = response.status;
      if (error || !data) {
        if (status === 404) {
          flashesStore.addErrorFlash({
            message: `Channel "${name}" not found`,
          });
        } else {
          captureException(
            error ?? new Error(`Channel lookup failed (status ${status})`),
          );
          flashesStore.addErrorFlash({
            message: `Couldn't verify channel "${name}", please try again`,
          });
        }
        return null;
      }
      if (requireShared && data.channel_type !== 'shared-channel') {
        flashesStore.addErrorFlash({
          message:
            'Only shared Slack channels can be used for partner notifications',
        });
        return null;
      }
      if (
        !customLookupChannels.value.some(
          (ch: ReportSlackChannel) => ch.id === data.id,
        )
      ) {
        customLookupChannels.value = [...customLookupChannels.value, data];
      }
      return data;
    } finally {
      isLookingUpChannel.value = false;
    }
  }

  return {
    isServerChannelSearch,
    isFetchingChannels,
    isLookingUpChannel,
    channelSearchQuery,
    cachedChannels,
    customLookupChannels,
    searchChannels,
    onChannelSearch,
    resetSearch,
    clearCustomChannels,
    lookUpCustomChannel,
    isChannelListSyncing,
    showSyncingState,
    hasNoSyncedChannels,
    hasSyncError,
  };
}
